import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import { filterMfeNames, filterMfeNamesByMajor, formatMfeDisplayName, getMfeMajors, isProductMfe } from "../product-utils";
import type { EnvironmentsResponse, Environment, PromotionChange, PromotionRequest, PromotionPreset, Snapshot, ProductsResponse, EntryVersion } from "../types";
import { getSnapshotTargetEnvironment } from "../snapshot-utils";
import { createPreset, loadPresets, MAX_PRESETS, persistPresets } from "../preset-utils";
import {
  getOutdatedMfes,
  getOutdatedEntries,
  getPromotionTargetForSource,
  isEnvironmentMissing,
  isEnvironmentOutdated,
  isEntryOutdated,
  isEntryMissing,
  type PromotionSourceEnvironment,
  type EntrySection,
} from "../components/environment-overview.logic";

import "../components/diff-preview-dialog";

const MFE_MAJOR_FILTER_STORAGE_KEY = "hawaii-dashboard-mfe-major-filter";

@customElement("versions-page")
export class VersionsPage extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex: 1;
      flex-direction: column;
      min-height: 0;
      font-family: Geist, sans-serif;
    }

    .controls {
      display: flex;
      align-items: center;
      gap: var(--spacing-12, 12px);
      margin-bottom: var(--spacing-16, 16px);
      flex-wrap: wrap;
    }

    .selection-controls {
      display: flex;
      gap: var(--spacing-16, 16px);
      align-items: center;
      margin-left: auto;
    }

    .selection-group {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
    }

    .selection-label {
      font-weight: 600;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      white-space: nowrap;
    }

    .cards {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-16, 16px);
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      margin-bottom: var(--spacing-16, 16px);
    }

    bee-card {
      --padding: 0;
    }

    bee-card::part(body) {
      display: flex;
      flex-direction: column;
      padding: 0;
    }

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--spacing-16, 16px) var(--spacing-16, 16px) 0;
    }

    .card-title-group {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
    }

    .card-title {
      font-weight: 600;
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .card-content {
      padding: 0 var(--spacing-16, 16px) var(--spacing-16, 16px);
    }

    .version-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }

    .version-table th {
      text-align: center;
      padding: var(--spacing-8, 8px) var(--spacing-12, 12px);
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      border-bottom: 2px solid var(--colors-border-border-gray-300, #e0e3e8);
    }

    .version-table th.name-col {
      text-align: left;
    }

    .version-table td {
      padding: var(--spacing-6, 6px) var(--spacing-12, 12px);
      border-bottom: 1px solid var(--colors-border-border-gray-300, #e0e3e8);
      font-size: 13px;
      vertical-align: middle;
    }

    .version-table tr:last-child td {
      border-bottom: none;
    }

    .env-col {
      text-align: center;
    }

    .promote-col {
      text-align: center;
      vertical-align: middle;
    }

    .cell-content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--spacing-8, 8px);
    }

    .item-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .product-row td {
      background: var(--colors-background-bg-brand-50, #f0f0ff);
    }

    .version {
      font-family: monospace;
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      white-space: nowrap;
    }

    .outdated {
      color: var(--colors-foreground-fg-warning-500, #ed6c02);
      font-weight: 600;
    }

    .missing {
      color: var(--colors-foreground-fg-danger-500, #c62828);
      font-weight: 600;
      font-style: italic;
    }

    .sentinel {
      color: var(--colors-foreground-fg-gray-500, #9ca3b0);
      font-style: italic;
    }

    .preset-select {
      min-width: 200px;
    }

    .promote-bar {
      display: flex;
      gap: var(--spacing-12, 12px);
      align-items: center;
      flex-shrink: 0;
      position: sticky;
      bottom: 0;
      padding: var(--spacing-16, 16px) 0;
      background: var(--colors-background-bg-primary, #fff);
      z-index: 1;
    }

    .promote-bar-actions {
      display: flex;
      gap: var(--spacing-12, 12px);
      align-items: center;
      margin-left: auto;
    }

    .empty {
      padding: var(--spacing-24, 24px);
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      text-align: center;
      font-size: 13px;
    }

    .empty-section {
      padding: var(--spacing-16, 16px);
      color: var(--colors-foreground-fg-gray-500, #9ca3b0);
      font-size: 12px;
      font-style: italic;
    }
  `;

  @property({ attribute: false }) products: ProductsResponse | null = null;
  @property({ attribute: false }) activeProductFilter: string | null = null;

  @state() private _data: EnvironmentsResponse | null = null;
  @state() private _loading = true;
  // MFE selections
  @state() private _selectedStage = new Set<string>();
  @state() private _selectedProd = new Set<string>();
  // Shell selections
  @state() private _selectedShellStage = new Set<string>();
  @state() private _selectedShellProd = new Set<string>();
  // Services selections
  @state() private _selectedServicesStage = new Set<string>();
  @state() private _selectedServicesProd = new Set<string>();

  @state() private _previewData: { changes: PromotionChange[] } | null = null;
  @state() private _previewRequest: PromotionRequest | null = null;
  @state() private _presets: PromotionPreset[] = [];
  @state() private _selectedStagePresetId = "";
  @state() private _selectedProdPresetId = "";
  // "" = All. Not reset by the product filter; may hold a major absent from current data.
  @state() private _mfeMajorFilter = "";

  updated(changedProperties: PropertyValues<this>) {
    if (changedProperties.has("activeProductFilter")) {
      this._resetProductScopedState();
    }
  }

  async connectedCallback() {
    super.connectedCallback();
    this._presets = loadPresets();
    this._mfeMajorFilter = this._loadPersistedMajorFilter();
    await this._refresh();
  }

  private _loadPersistedMajorFilter(): string {
    try {
      return localStorage.getItem(MFE_MAJOR_FILTER_STORAGE_KEY) ?? "";
    } catch {
      return "";
    }
  }

  private _setMajorFilter(major: string) {
    this._mfeMajorFilter = major;
    try {
      if (major) {
        localStorage.setItem(MFE_MAJOR_FILTER_STORAGE_KEY, major);
      } else {
        localStorage.removeItem(MFE_MAJOR_FILTER_STORAGE_KEY);
      }
    } catch {
      // Major filter persistence is non-critical — silently degrade
    }
  }

  // A stored major absent from current data shows All but stays stored.
  private _getActiveMajor(): string {
    return getMfeMajors(this._getMfesByEnv()).includes(this._mfeMajorFilter) ? this._mfeMajorFilter : "";
  }

  private _getMfesByEnv() {
    return this._data ? [this._data.dev.mfes, this._data.stage.mfes, this._data.prod.mfes] : [];
  }

  private _getSelection(target: "stage" | "prod"): Set<string> {
    return target === "stage" ? this._selectedStage : this._selectedProd;
  }

  private _getShellSelection(target: "stage" | "prod"): Set<string> {
    return target === "stage" ? this._selectedShellStage : this._selectedShellProd;
  }

  private _getServicesSelection(target: "stage" | "prod"): Set<string> {
    return target === "stage" ? this._selectedServicesStage : this._selectedServicesProd;
  }



  private _resetProductScopedState() {
    this._selectedStage = new Set();
    this._selectedProd = new Set();
    this._selectedShellStage = new Set();
    this._selectedShellProd = new Set();
    this._selectedServicesStage = new Set();
    this._selectedServicesProd = new Set();
    this._previewData = null;
    this._previewRequest = null;
    this._selectedStagePresetId = "";
    this._selectedProdPresetId = "";
  }

  private _setSelection(target: "stage" | "prod", selection: Set<string>) {
    if (target === "stage") {
      this._selectedStage = selection;
    } else {
      this._selectedProd = selection;
    }
  }

  private _setShellSelection(target: "stage" | "prod", selection: Set<string>) {
    if (target === "stage") {
      this._selectedShellStage = selection;
    } else {
      this._selectedShellProd = selection;
    }
  }

  private _setServicesSelection(target: "stage" | "prod", selection: Set<string>) {
    if (target === "stage") {
      this._selectedServicesStage = selection;
    } else {
      this._selectedServicesProd = selection;
    }
  }



  private _showToast(message: string, variant: "error" | "warning" | "success" | "neutral" = "error") {
    const alert = Object.assign(document.createElement("bee-alert"), {
      variant,
      closable: true,
      duration: 5000,
    }) as HTMLElement & { toast(): Promise<void> };
    const msgSpan = document.createElement("span");
    msgSpan.slot = "message";
    msgSpan.textContent = message;
    alert.append(msgSpan);
    document.body.append(alert);
    alert.toast();
  }

  private async _refresh() {
    this._loading = true;
    try {
      const [envData, servicesData] = await Promise.all([
        api.getEnvironments(),
        api.getServices().catch(() => null),
      ]);
      // Merge services into environment data: preprod→stage, prod→prod
      if (servicesData) {
        envData.stage.services = servicesData.preprod;
        envData.prod.services = servicesData.prod;
      }
      this._data = envData;
      this.dispatchEvent(
        new CustomEvent<EnvironmentsResponse>("environments-change", {
          detail: this._data,
          bubbles: true,
          composed: true,
        }),
      );
    } catch (e: unknown) {
      this._showToast(e instanceof Error ? e.message : "Failed to load environments");
    } finally {
      this._loading = false;
    }
  }

  private _toggleSelection(sourceEnv: PromotionSourceEnvironment, mfeName: string) {
    const target = getPromotionTargetForSource(sourceEnv);
    if (!target) return;

    const set = new Set(this._getSelection(target));
    if (set.has(mfeName)) {
      set.delete(mfeName);
    } else {
      set.add(mfeName);
    }

    this._setSelection(target, set);
  }

  private _toggleEntrySelection(sourceEnv: PromotionSourceEnvironment, name: string, section: EntrySection) {
    const target = getPromotionTargetForSource(sourceEnv);
    if (!target) return;

    const getter = section === "shell" ? this._getShellSelection : this._getServicesSelection;
    const setter = section === "shell"
      ? (t: "stage" | "prod", s: Set<string>) => this._setShellSelection(t, s)
      : (t: "stage" | "prod", s: Set<string>) => this._setServicesSelection(t, s);

    const set = new Set(getter.call(this, target));
    if (set.has(name)) {
      set.delete(name);
    } else {
      set.add(name);
    }
    setter(target, set);
  }

  private _applyPreset(preset: PromotionPreset, target: "stage" | "prod") {
    if (!this._data) return;

    const envName = target === "stage" ? "dev" : "stage";
    const envData = this._data[envName];
    const existingMfes = new Set(Object.keys(envData.mfes));
    const applicable = preset.mfes.filter((mfe) => existingMfes.has(mfe));

    this._setSelection(target, new Set(applicable));
  }

  private _getSelectedPresetId(target: "stage" | "prod") {
    return target === "stage" ? this._selectedStagePresetId : this._selectedProdPresetId;
  }

  private _setSelectedPresetId(target: "stage" | "prod", presetId: string) {
    if (target === "stage") {
      this._selectedStagePresetId = presetId;
    } else {
      this._selectedProdPresetId = presetId;
    }
  }

  private _handlePresetSelect(target: "stage" | "prod", e: CustomEvent) {
    const presetId = (e.target as HTMLSelectElement).value;
    this._setSelectedPresetId(target, presetId);

    if (!presetId) return;

    const preset = this._presets.find((item) => item.id === presetId);
    if (!preset) return;

    this._applyPreset(preset, target);
  }

  private _selectAllOutdated(sourceEnv: PromotionSourceEnvironment) {
    if (!this._data) return;

    const target = getPromotionTargetForSource(sourceEnv);
    if (!target) return;

    const outdatedMfes = filterMfeNamesByMajor(
      filterMfeNames(getOutdatedMfes(this._data, sourceEnv), this.activeProductFilter, this.products),
      this._getActiveMajor(),
      this._getMfesByEnv(),
    );
    this._setSelection(target, new Set(outdatedMfes));
    this._setShellSelection(target, new Set(getOutdatedEntries(this._data, "shell", sourceEnv)));
    this._setServicesSelection(target, new Set(getOutdatedEntries(this._data, "services", sourceEnv)));
  }

  private _clearSelection(sourceEnv: PromotionSourceEnvironment) {
    const target = getPromotionTargetForSource(sourceEnv);
    if (!target) return;

    this._setSelection(target, new Set());
    this._setShellSelection(target, new Set());
    this._setServicesSelection(target, new Set());
  }

  private _getTotalSelectionCount(target: "stage" | "prod"): number {
    return this._getSelection(target).size
      + this._getShellSelection(target).size
      + this._getServicesSelection(target).size;
  }

  private _hasAnySelection(sourceEnv: PromotionSourceEnvironment): boolean {
    const target = getPromotionTargetForSource(sourceEnv);
    if (!target) return false;

    return this._getTotalSelectionCount(target) > 0;
  }

  private _toggleOutdatedSelection(sourceEnv: PromotionSourceEnvironment) {
    if (this._hasAnySelection(sourceEnv)) {
      this._clearSelection(sourceEnv);
      return;
    }

    this._selectAllOutdated(sourceEnv);
  }

  private async _handlePromote(target: "stage" | "prod") {
    const source: Environment = target === "stage" ? "dev" : "stage";
    const mfeSelected = [...this._getSelection(target)];
    const shellSelected = [...this._getShellSelection(target)];
    const servicesSelected = [...this._getServicesSelection(target)];

    if (mfeSelected.length + shellSelected.length + servicesSelected.length === 0) return;

    await this._openPreview({
      source,
      target,
      mfes: mfeSelected,
      ...(shellSelected.length > 0 ? { shell: shellSelected } : {}),
      ...(servicesSelected.length > 0 ? { services: servicesSelected } : {}),
      ...(this.activeProductFilter ? { product: this.activeProductFilter } : {}),
    });
  }

  private async _openPreview(request: PromotionRequest) {
    try {
      const preview = await api.previewPromotion(request);
      if (preview.changes.length === 0) {
        this._showToast("No changes to promote -- all selections are already up to date.", "warning");
        return;
      }
      this._previewData = { changes: preview.changes };
      this._previewRequest = request;
    } catch (e: unknown) {
      this._showToast(e instanceof Error ? e.message : "Failed to preview promotion");
    }
  }

  private _handlePreviewClose() {
    this._previewData = null;
    this._previewRequest = null;
  }

  private async _handlePreviewConfirm() {
    this._selectedStage = new Set();
    this._selectedProd = new Set();
    this._selectedShellStage = new Set();
    this._selectedShellProd = new Set();
    this._selectedServicesStage = new Set();
    this._selectedServicesProd = new Set();
  }

  public async applySnapshot(snapshot: Snapshot) {
    const target = getSnapshotTargetEnvironment(snapshot.environment);
    if (!target) {
      this._showToast("Prod snapshots do not have a downstream environment to promote into.");
      return;
    }

    await this._openPreview({
      source: snapshot.environment,
      target,
      mfes: Object.keys(snapshot.versions).sort(),
      sourceVersions: snapshot.versions,
      snapshotName: snapshot.name,
      snapshotTimestamp: snapshot.timestamp,
    });
  }

  private _renderPresetSelect(target: "stage" | "prod") {
    const selectedPresetId = this._getSelectedPresetId(target);
    return html`
      <bee-select
        class="preset-select"
        size="small"
        .value=${selectedPresetId}
        ?disabled=${this._presets.length === 0}
        @bee-change=${(e: CustomEvent) => this._handlePresetSelect(target, e)}
      >
        <bee-option value="">${this._presets.length === 0 ? "No presets available" : "Apply preset..."}</bee-option>
        ${this._presets.map((preset) => html`
          <bee-option value=${preset.id}>${preset.name} (${preset.mfes.length})</bee-option>
        `)}
      </bee-select>
    `;
  }

  private _getSelectedMfes(): string[] {
    if (this._selectedStage.size > 0) return [...this._selectedStage];
    if (this._selectedProd.size > 0) return [...this._selectedProd];
    return [];
  }

  private _savePreset() {
    const mfes = this._getSelectedMfes();
    if (mfes.length === 0) return;
    if (this._presets.length >= MAX_PRESETS) return;

    const now = new Date();
    const name = `preset ${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-${String(now.getUTCDate()).padStart(2, "0")} ${String(now.getUTCHours()).padStart(2, "0")}:${String(now.getUTCMinutes()).padStart(2, "0")}:${String(now.getUTCSeconds()).padStart(2, "0")}`;
    const preset = createPreset(name, mfes);
    this._presets = [preset, ...this._presets];
    persistPresets(this._presets);
    this.dispatchEvent(new CustomEvent("navigate", { detail: "/presets", bubbles: true, composed: true }));
  }

  private _renderSavePresetButton() {
    const totalSelected = this._selectedStage.size + this._selectedProd.size;
    if (totalSelected === 0) return nothing;

    const atLimit = this._presets.length >= MAX_PRESETS;

    return html`
      <bee-button
        label="Save Selection as Preset (${totalSelected})"
        bee-type="secondary-grey"
        size="medium"
        ?disabled=${atLimit}
        @click=${() => this._savePreset()}
      ></bee-button>
    `;
  }

  private _getAllMfeNames(): string[] {
    if (!this._data) return [];
    return [...new Set([
      ...Object.keys(this._data.dev.mfes),
      ...Object.keys(this._data.stage.mfes),
      ...Object.keys(this._data.prod.mfes),
    ])].sort();
  }

  private _getAllEntryNames(section: EntrySection): string[] {
    if (!this._data) return [];
    return [...new Set([
      ...Object.keys(this._data.dev[section] ?? {}),
      ...Object.keys(this._data.stage[section] ?? {}),
      ...Object.keys(this._data.prod[section] ?? {}),
    ])].sort();
  }

  private _renderMfeRow(name: string) {
    if (!this._data) return nothing;
    const devMfe = this._data.dev.mfes[name];
    const stageMfe = this._data.stage.mfes[name];
    const prodMfe = this._data.prod.mfes[name];

    const stageMissing = isEnvironmentMissing(this._data, name, "stage");
    const stageOutdated = !stageMissing && isEnvironmentOutdated(this._data, name, "stage");

    const prodMissing = isEnvironmentMissing(this._data, name, "prod");
    const prodOutdated = !prodMissing && isEnvironmentOutdated(this._data, name, "prod");

    const isProduct = isProductMfe(name, this.products);

    return html`
      <tr class=${isProduct ? "product-row" : ""}>
        <td class="name-col"><span class="item-name" title="${name}">${formatMfeDisplayName(name)}</span></td>
        <td class="env-col">
          <div class="cell-content">
            <span class="version">${devMfe?.resolved ?? ""}</span>
          </div>
        </td>
        <td class="promote-col">
          <bee-checkbox
            .checked=${this._selectedStage.has(name)}
            size="small"
            ?disabled=${!devMfe}
            @bee-change=${() => this._toggleSelection("dev", name)}
          ></bee-checkbox>
        </td>
        <td class="env-col">
          <div class="cell-content">
            <span class="version ${stageMissing ? "missing" : stageOutdated ? "outdated" : ""}">${stageMissing ? "" : stageMfe?.resolved ?? ""}</span>
          </div>
        </td>
        <td class="promote-col">
          <bee-checkbox
            .checked=${this._selectedProd.has(name)}
            size="small"
            ?disabled=${!stageMfe || stageMissing}
            @bee-change=${() => this._toggleSelection("stage", name)}
          ></bee-checkbox>
        </td>
        <td class="env-col">
          <div class="cell-content">
            <span class="version ${prodMissing ? "missing" : prodOutdated ? "outdated" : ""}">${prodMissing ? "" : prodMfe?.resolved ?? ""}</span>
          </div>
        </td>
      </tr>
    `;
  }

  private _renderEntryRow(name: string, section: EntrySection) {
    if (!this._data) return nothing;
    const devEntry = this._data.dev[section]?.[name];
    const stageEntry = this._data.stage[section]?.[name];
    const prodEntry = this._data.prod[section]?.[name];

    const stageMissing = isEntryMissing(this._data, name, section, "stage");
    const stageOutdated = !stageMissing && isEntryOutdated(this._data, name, section, "stage");
    const devIsSentinel = devEntry?.version === "0.0.0";

    const prodMissing = isEntryMissing(this._data, name, section, "prod");
    const prodOutdated = !prodMissing && isEntryOutdated(this._data, name, section, "prod");
    const stageIsSentinel = stageEntry?.version === "0.0.0";

    const selStage = section === "shell" ? this._getShellSelection("stage") : this._getServicesSelection("stage");
    const selProd = section === "shell" ? this._getShellSelection("prod") : this._getServicesSelection("prod");

    return html`
      <tr>
        <td class="name-col"><span class="item-name" title="${name}">${name}</span></td>
        <td class="env-col">
          ${section !== "services" ? html`<div class="cell-content">
            <span class="version ${devIsSentinel ? "sentinel" : ""}">${devEntry?.version ?? ""}</span>
          </div>` : nothing}
        </td>
        <td class="promote-col">
          ${section !== "services" ? html`<bee-checkbox
            .checked=${selStage.has(name)}
            size="small"
            ?disabled=${!devEntry || devIsSentinel}
            @bee-change=${() => this._toggleEntrySelection("dev", name, section)}
          ></bee-checkbox>` : nothing}
        </td>
        <td class="env-col">
          <div class="cell-content">
            <span class="version ${stageMissing ? "missing" : stageOutdated ? "outdated" : stageIsSentinel ? "sentinel" : ""}">${stageMissing ? "" : stageEntry?.version ?? ""}</span>
          </div>
        </td>
        <td class="promote-col">
          <bee-checkbox
            .checked=${selProd.has(name)}
            size="small"
            ?disabled=${!stageEntry || stageMissing || stageIsSentinel}
            @bee-change=${() => this._toggleEntrySelection("stage", name, section)}
          ></bee-checkbox>
        </td>
        <td class="env-col">
          <div class="cell-content">
            <span class="version ${prodMissing ? "missing" : prodOutdated ? "outdated" : ""}">${prodMissing ? "" : prodEntry?.version ?? ""}</span>
          </div>
        </td>
      </tr>
    `;
  }

  private _renderCategoryCard(
    title: string,
    names: string[],
    category: "shell" | "mfes" | "services",
    headerExtra: unknown = nothing,
  ) {
    const colLabel = category === "mfes" ? "MFE" : "Component";
    const isServices = category === "services";

    return html`
      <bee-card type="neutral">
        <div slot="header" class="card-header">
          <span class="card-title-group">
            <span class="card-title">${title}</span>
            ${title === "Hawaii" || title === "Services" ? html`<bee-tag color="brand" small>Global</bee-tag>` : nothing}
          </span>
          ${headerExtra}
        </div>
        <div class="card-content">
          ${names.length === 0
            ? html`<div class="empty-section">No data</div>`
            : html`
              <table class="version-table">
                <thead>
                  <tr>
                    <th class="name-col">${colLabel}</th>
                    <th class="env-col">${isServices ? "" : "Dev"}</th>
                    <th class="promote-col">${isServices ? "" : "→"}</th>
                    <th class="env-col">${isServices ? "Preprod" : "Stage"}</th>
                    <th class="promote-col">→</th>
                    <th class="env-col">Prod</th>
                  </tr>
                </thead>
                <tbody>
                  ${category === "mfes"
                    ? names.map((name) => this._renderMfeRow(name))
                    : names.map((name) => this._renderEntryRow(name, category))}
                </tbody>
              </table>
            `}
        </div>
      </bee-card>
    `;
  }

  render() {
    if (this._loading) {
      return html`<div class="empty"><bee-spinner size="medium" type="blue"></bee-spinner></div>`;
    }

    const stageTotal = this._getTotalSelectionCount("stage");
    const prodTotal = this._getTotalSelectionCount("prod");
    const hasDevSelection = this._hasAnySelection("dev");
    const hasStageSelection = this._hasAnySelection("stage");

    const allMfeNames = this._getAllMfeNames();
    const majors = getMfeMajors(this._getMfesByEnv());
    const activeMajor = this._getActiveMajor();
    const mfeNames = filterMfeNamesByMajor(
      filterMfeNames(allMfeNames, this.activeProductFilter, this.products),
      activeMajor,
      this._getMfesByEnv(),
    );
    mfeNames.sort((a, b) => {
      const aIsProduct = isProductMfe(a, this.products);
      const bIsProduct = isProductMfe(b, this.products);
      if (aIsProduct !== bIsProduct) return aIsProduct ? -1 : 1;
      return a.localeCompare(b);
    });
    const shellNames = this._getAllEntryNames("shell");
    const servicesNames = this._getAllEntryNames("services");

    const majorFilter = html`
      <div class="selection-group">
        <span class="selection-label">Major version</span>
        <bee-select
          class="major-select"
          aria-label="Major version"
          size="small"
          .value=${activeMajor}
          @bee-change=${(e: CustomEvent) => this._setMajorFilter((e.target as HTMLSelectElement).value)}
        >
          <bee-option value="">All</bee-option>
          ${majors.map((major) => html`<bee-option value=${major}>${major}</bee-option>`)}
        </bee-select>
      </div>
    `;

    return html`
      <div class="controls">
        <bee-button label="Refresh" bee-type="secondary-grey" size="small" icon="refresh" @click=${() => this._refresh()}></bee-button>
        <div class="selection-controls">
          <div class="selection-group">
            <span class="selection-label">Dev → Stage</span>
            <bee-button
              label=${hasDevSelection ? "Clear" : "Select Outdated"}
              bee-type=${hasDevSelection ? "destructive-tertiary" : "secondary-grey"}
              size="small"
              @click=${() => this._toggleOutdatedSelection("dev")}
            ></bee-button>
            ${this._renderPresetSelect("stage")}
            ${stageTotal > 0 ? html`<bee-tag color="brand" small>${stageTotal} selected</bee-tag>` : nothing}
          </div>
          <div class="selection-group">
            <span class="selection-label">Stage → Prod</span>
            <bee-button
              label=${hasStageSelection ? "Clear" : "Select Outdated"}
              bee-type=${hasStageSelection ? "destructive-tertiary" : "secondary-grey"}
              size="small"
              @click=${() => this._toggleOutdatedSelection("stage")}
            ></bee-button>
            ${this._renderPresetSelect("prod")}
            ${prodTotal > 0 ? html`<bee-tag color="brand" small>${prodTotal} selected</bee-tag>` : nothing}
          </div>
        </div>
      </div>
      <div class="cards">
        ${this._renderCategoryCard("Hawaii", shellNames, "shell")}
        ${this._renderCategoryCard("Micro-Frontends", mfeNames, "mfes", majorFilter)}
        ${this._renderCategoryCard("Services", servicesNames, "services")}
      </div>
      <div class="promote-bar">
        ${this._renderSavePresetButton()}
        <div class="promote-bar-actions">
          <bee-button
            label="Promote to Stage (${stageTotal})"
            bee-type="primary"
            size="medium"
            ?disabled=${stageTotal === 0}
            @click=${() => this._handlePromote("stage")}
          ></bee-button>
          <bee-button
            label="Promote to Prod (${prodTotal})"
            bee-type="destructive"
            size="medium"
            ?disabled=${prodTotal === 0}
            @click=${() => this._handlePromote("prod")}
          ></bee-button>
        </div>
      </div>

      ${this._previewData
        ? html`<diff-preview-dialog
            .changes=${this._previewData.changes}
            .request=${this._previewRequest ?? { source: "dev", target: "stage", mfes: [] }}
            .environmentData=${this._data}
            .products=${this.products}
            @close=${() => this._handlePreviewClose()}
            @confirmed=${() => this._handlePreviewConfirm()}
          ></diff-preview-dialog>`
        : ""}
    `;
  }
}
